// components/Timeline.tsx
import type { TimelineProps } from '@/sections/threats/types';
import {
  calculateTimelinePosition,
  formatTimelineDate,
} from '@/sections/threats/utils';
import { cn } from '@/utils/classname';

/**
 * Horizontal timeline visualization with collision-aware positioning
 */
export function Timeline({ events }: TimelineProps) {
  if (events.length === 0) {
    return (
      <p className="text-sm">
        No publishing or update information available at this time
      </p>
    );
  }

  const firstDate = new Date(events[0].date);
  const lastDate = new Date(events[events.length - 1].date);

  return (
    <div
      className="relative min-h-[250px] pr-8"
      role="list"
      aria-label={`Timeline with ${events.length} events`}
    >
      {/* Main horizontal line */}
      <div
        className="bg-chariot-stroke absolute top-[86px] left-0 h-[2px] w-full"
        aria-hidden="true"
      />

      {/* Timeline events */}
      <div className="relative flex min-h-[250px] justify-between overflow-x-auto">
        {events.map((event, index) => {
          const position = calculateTimelinePosition(
            new Date(event.date),
            firstDate,
            lastDate,
            index,
            events.length
          );
          const formattedDate = formatTimelineDate(event.date);

          return (
            <div
              key={`${event.date}-${event.title}`}
              role="listitem"
              aria-label={`${event.title} on ${formattedDate}`}
              className={cn(
                'absolute flex max-w-[140px] min-w-[140px] flex-col items-center gap-2',
                index % 2 !== 0 && 'flex-col-reverse'
              )}
              style={{ left: `${position}%` }}
            >
              <div className="flex flex-col items-center gap-1">
                <div className="text-chariot-text-primary text-sm font-medium text-nowrap">
                  {formattedDate}
                </div>
                <div className="text-chariot-text-secondary line-clamp-2 min-h-8 max-w-[140px] text-center text-xs">
                  {event.title}
                </div>
              </div>

              {/* Dot connector */}
              <div
                className={cn(
                  'flex flex-col items-center',
                  index % 2 !== 0 && 'flex-col-reverse'
                )}
              >
                <div className="bg-chariot-brand-primary size-1.5 rounded-full" />
                <div className="bg-chariot-brand-primary h-2 w-[2px]" />
                <div className="bg-chariot-brand-primary size-4 rounded-full" />
              </div>

              {/* Spacer */}
              <div className="h-[70px] w-[2px]" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
